import { FC, useEffect, useMemo, useState } from 'react';

import { ResourceTypeExplorer } from '@dbt-labs/dbt-dag';
import { CellContext, Link } from '@dbt-labs/sourdough';

import { Page } from '@/components/Page';
import { ResourceChip } from '@/components/ResourceChip';
import { DetailsSection } from '@/components/SectionWithCard';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { usePackage } from '@/hooks/dbtStrategy/usePackage';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams, useValidatedParams } from '@/hooks/params';
import { PageInfo } from '@/reducers/usePaginatedQuery';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { ResourceDetailsHeader, TextIconPair } from '../../details/components/Header';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type PackageTableData = {
  uniqueId: string;
  name: string;
  resourceType: ResourceTypeExplorer;
};

type PackageResource = {
  uniqueId: ParsedUniqueId;
};

const getTableData = ({ uniqueId }: PackageResource): PackageTableData => {
  return {
    uniqueId: uniqueId.raw,
    name: uniqueId.remainder,
    resourceType: uniqueId.resourceType,
  };
};

export const PackageFilterPage: FC = () => {
  const { packageName } = useValidatedParams();
  const { accountId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const getPackageDetailsPageLink = useLinkToExplorer('packageDetails');

  const packageQuery = usePackage(packageName);

  const pageSize = INITIAL_PAGE_SIZE;
  const [shownResults, setShownResults] = useState(INITIAL_PAGE_SIZE);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    endCursor: null,
    hasNextPage: false,
    totalCount: null,
  });
  const isInitialLoad = packageQuery === undefined;

  useEffect(() => {
    const len = packageQuery.data?.applied?.resources?.length;
    const totalCount = len == null ? null : len;
    setPageInfo({
      endCursor: null,
      hasNextPage: (totalCount || 0) > shownResults,
      totalCount,
    });
  }, [packageQuery.data?.applied?.resources?.length, shownResults]);

  useEffect(() => {
    setShownResults(pageSize);
  }, [packageName, pageSize]);

  const data = useMemo(() => {
    return packageQuery.data?.applied?.resources
      ?.map(getTableData)
      .slice(0, shownResults);
  }, [packageQuery.data?.applied?.resources, shownResults]);

  if (!packageName || !accountId) return null;

  const headerIcons: TextIconPair[] = [];
  return (
    <Page title={packageName} pageType="packageDetails">
      <ResourceDetailsHeader
        headerIcons={headerIcons}
        title={packageName}
        packageName={packageName}
        files={[]}
        name={packageName}
        resourceType="package"
        updatedDate={undefined}
      />
      <DetailsSection heading="Package Resources" withCard={false}>
        <ResourceFilterTable
          pluralizedResultName="resources"
          data={data}
          columns={[
            {
              header: 'Resource type',
              accessorKey: 'resourceType',
              minSize: 210,
              size: 10,
              cell: ({ getValue }) => (
                <span className="space-x-1 whitespace-nowrap">
                  <ResourceChip resourceType={getValue()} />
                </span>
              ),
            },
            {
              header: 'Name',
              accessorKey: 'name',
              id: 'name',
              cell: ({
                getValue,
                row: {
                  original: { name },
                },
              }: CellContext<PackageTableData, any>) => (
                <Link
                  isInternal
                  to={getPackageDetailsPageLink({
                    ...projectScope,
                    packageName: name,
                  })}
                >
                  {getValue()}
                </Link>
              ),
              size: 100,
              minSize: 170,
            },
          ]}
          pageInfo={pageInfo}
          isLoading={isInitialLoad}
          // Pagination is faked clientside, there are no more results to load
          isLoadingMore={false}
          pageSize={pageSize}
          fetchMore={() => setShownResults((prev) => prev + pageSize)}
        />
      </DetailsSection>
    </Page>
  );
};
